import React, { useState, useEffect } from "react";
import "./AvailableAirlineConfig.css";
import axios from "axios";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Switch from "@mui/material/Switch";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import Snackbar from "@mui/material/Snackbar";
import { styled } from "@mui/material/styles";
import Loader from "../../Components/Loader/Loader";

const BASE_URL = "https://api.aeroprime.in/crm-service/admin";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#334555",
    color: theme.palette.common.white,
    fontWeight: 500,
    fontSize: 13,
  },
}));

const RedSwitch = styled(Switch)(() => ({
  "& .MuiSwitch-switchBase.Mui-checked": {
    color: "#ef5443",
    "&:hover": { backgroundColor: "rgba(239,84,67,0.08)" },
  },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: "#ef5443",
  },
}));

const EMPTY_FORM = { airlineCode: "", airlineName: "", isActive: true };

const AvailableAirlineConfig = () => {
  const [showLoader, setShowLoader] = useState(false);
  const [airlines, setAirlines] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [snack, setSnack] = useState({ open: false, msg: "", type: "success" });

  const getHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: localStorage.getItem("AuthToken"),
  });

  const handle401 = (err) => {
    if (err?.response?.status === 401) {
      localStorage.clear();
      window.location.href = "/";
    }
  };

  const showSnack = (msg, type = "success") =>
    setSnack({ open: true, msg, type });

  const fetchAirlines = () => {
    setShowLoader(true);
    axios
      .get(`${BASE_URL}/airlineConfigs`, { headers: getHeaders() })
      .then((res) => {
        setAirlines(res.data || []);
        setShowLoader(false);
      })
      .catch((err) => {
        setShowLoader(false);
        handle401(err);
        showSnack("Failed to load airline list.", "error");
      });
  };

  useEffect(() => {
    fetchAirlines();
  }, []);

  const toggleAirline = (id) => {
    axios
      .put(`${BASE_URL}/airlineConfig/${id}/toggle`, {}, { headers: getHeaders() })
      .then((res) => {
        showSnack(res.data?.message || "Updated.");
        fetchAirlines();
      })
      .catch((err) => {
        handle401(err);
        showSnack("Failed to toggle airline.", "error");
      });
  };

  const deleteAirline = (id, code) => {
    if (!window.confirm(`Remove airline "${code}" from the list? This cannot be undone.`)) return;
    axios
      .delete(`${BASE_URL}/airlineConfig/${id}`, { headers: getHeaders() })
      .then((res) => {
        showSnack(res.data?.message || "Deleted.");
        fetchAirlines();
      })
      .catch((err) => {
        handle401(err);
        showSnack("Failed to delete airline.", "error");
      });
  };

  const saveAirline = () => {
    if (!form.airlineCode.trim()) {
      showSnack("Airline code is required.", "error");
      return;
    }
    axios
      .post(
        `${BASE_URL}/airlineConfig`,
        {
          airlineCode: form.airlineCode.trim().toUpperCase(),
          airlineName: form.airlineName.trim() || null,
          isActive: form.isActive,
        },
        { headers: getHeaders() }
      )
      .then((res) => {
        if (res.data?.success) {
          showSnack(res.data.message || "Saved.");
          setShowAddForm(false);
          setForm(EMPTY_FORM);
          fetchAirlines();
        } else {
          showSnack(res.data?.message || "Failed.", "error");
        }
      })
      .catch((err) => {
        handle401(err);
        showSnack("Failed to save airline.", "error");
      });
  };

  return (
    <div className="aa-wrapper">
      {showLoader && <Loader hideLoader={false} />}

      <div className="aa-heading">AVAILABLE AIRLINE CONFIG</div>
      <div className="aa-break" />

      <div className="aa-section-block">
        <div className="aa-section-header">
          <span className="aa-section-title">
            Airlines &nbsp;
            <span style={{ fontWeight: 400, fontSize: 13, color: "#888", textTransform: "none", letterSpacing: 0 }}>
              ({airlines.filter((a) => a.isActive).length} active / {airlines.length} total)
            </span>
          </span>
          <button className="aa-add-btn" onClick={() => setShowAddForm((v) => !v)}>
            + Add Airline
          </button>
        </div>

        <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e0e0e0", borderRadius: "8px" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <StyledTableCell>Airline Code</StyledTableCell>
                <StyledTableCell>Airline Name</StyledTableCell>
                <StyledTableCell>Status</StyledTableCell>
                <StyledTableCell>Updated At</StyledTableCell>
                <StyledTableCell>Actions</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {airlines.length > 0 ? (
                airlines.map((a) => (
                  <TableRow key={a.id} hover>
                    <TableCell className="aa-code-mono">{a.airlineCode}</TableCell>
                    <TableCell style={{ color: "#555", fontSize: 13 }}>{a.airlineName || "—"}</TableCell>
                    <TableCell>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <RedSwitch
                          size="small"
                          checked={!!a.isActive}
                          onChange={() => toggleAirline(a.id)}
                        />
                        <span className={`aa-badge ${a.isActive ? "aa-badge-active" : "aa-badge-inactive"}`}>
                          {a.isActive ? "Active" : "Inactive"}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell style={{ color: "#888", fontSize: 12 }}>
                      {a.updatedAt
                        ? new Date(a.updatedAt).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })
                        : "—"}
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => deleteAirline(a.id, a.airlineCode)}>
                        <DeleteIcon fontSize="small" style={{ color: "#ef5443" }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5}>
                    <div className="aa-empty">No airlines configured. Add one to get started.</div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {showAddForm && (
          <div className="aa-add-form">
            <h4>Add / Update Airline</h4>
            <div className="aa-form-grid">
              <div className="aa-form-group">
                <label>Airline Code *</label>
                <input
                  className="aa-form-input mono"
                  placeholder="e.g. C6"
                  maxLength={10}
                  value={form.airlineCode}
                  onChange={(e) => setForm((f) => ({ ...f, airlineCode: e.target.value }))}
                />
              </div>
              <div className="aa-form-group">
                <label>Airline Name</label>
                <input
                  className="aa-form-input"
                  placeholder="e.g. IndiGo"
                  value={form.airlineName}
                  onChange={(e) => setForm((f) => ({ ...f, airlineName: e.target.value }))}
                />
              </div>
              <div className="aa-form-group">
                <label>Status</label>
                <select
                  className="aa-form-select"
                  value={form.isActive ? "true" : "false"}
                  onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.value === "true" }))}
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>
            </div>
            <div className="aa-form-actions">
              <button className="aa-save-btn" onClick={saveAirline}>Save</button>
              <button className="aa-cancel-btn" onClick={() => { setShowAddForm(false); setForm(EMPTY_FORM); }}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        message={snack.msg}
        ContentProps={{
          className: snack.type === "error" ? "aa-snack-error" : "aa-snack-success",
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </div>
  );
};

export default AvailableAirlineConfig;
